import React from 'react';
import { ChevronLeft, ChevronRight } from '../../../icons/icons';
import { FloatingLabelSelect } from './OutlinedSelect';

interface CustomTimePickerProps {
  selectedDates: Date[];
  selectedTimes: { [key: string]: string[] };
  onTimeSelect: (date: Date, time: string) => void;
  error?: boolean;
  helperText?: string;
  availableSlots?: { [key: string]: string[] };
  disabledSlots?: { [key: string]: string[] };
  meetingDuration?: number;
}

const formatDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(date.getDate()).padStart(2, '0')}`;

function toTimeStr(date: Date) {
  return date
    .toTimeString()
    .slice(0, 5)
    .replace(/^(\d):/, '0$1');
}

interface TimeSlot {
  start: string;
  end: string;
  display: string;
}

const generateTimeSlots = (
  selectedDuration: number,
): {
  mainSlots: TimeSlot[];
  earlySlots: TimeSlot[];
  lateSlots: TimeSlot[];
} => {
  const mainSlots: TimeSlot[] = [];
  const earlySlots: TimeSlot[] = [];
  const lateSlots: TimeSlot[] = [];

  // Set interval based on selected duration
  const interval = selectedDuration >= 30 ? 30 : 15;

  const generateSlot = (hour: number, minute: number): TimeSlot => {
    const start = toTimeStr(new Date(0, 0, 0, hour, minute));
    const end = toTimeStr(new Date(0, 0, 0, hour, minute + selectedDuration));
    return {
      start,
      end,
      display: `${start} - ${end}`,
    };
  };

  for (let hour = 3; hour < 6; hour++) {
    for (let m = 0; m < 60; m += interval) {
      earlySlots.push(generateSlot(hour, m));
    }
  }

  for (let hour = 6; hour < 18; hour++) {
    for (let m = 0; m < 60; m += interval) {
      mainSlots.push(generateSlot(hour, m));
    }
  }

  for (let hour = 18; hour < 21; hour++) {
    for (let m = 0; m < 60; m += interval) {
      lateSlots.push(generateSlot(hour, m));
    }
  }

  return { mainSlots, earlySlots, lateSlots };
};

const durationOptions = [
  { label: '15 minutes', value: '15' },
  { label: '30 minutes', value: '30' },
  { label: '60 minutes', value: '60' },
  { label: '120 minutes', value: '120' },
];

export const CustomTimePicker: React.FC<CustomTimePickerProps> = ({
  selectedDates,
  selectedTimes,
  onTimeSelect,
  error,
  helperText,
  availableSlots = {},
  disabledSlots = {},
  meetingDuration = 30,
}) => {
  const [tab, setTab] = React.useState<'Main' | 'Early' | 'Late'>('Main');
  const [duration, setDuration] = React.useState<string>(
    meetingDuration.toString(),
  );
  const [idx, setIdx] = React.useState<number>(0);

  const { mainSlots, earlySlots, lateSlots } = generateTimeSlots(
    Number(duration),
  );
  if (selectedDates.length === 0) {
    return (
      <div className='w-full p-6 border rounded-lg shadow-md bg-white mt-5'>
        <h2 className='text-lg text-center font-semibold mb-4'>
          Select Time Slots
        </h2>
        <p className='text-gray-500'>No dates selected.</p>
      </div>
    );
  }
  const date = selectedDates[idx];
  const dateKey = formatDateKey(date);
  const slots = { Main: mainSlots, Early: earlySlots, Late: lateSlots }[tab];
  const selTimes = selectedTimes[dateKey] || [];
  const booked = (disabledSlots[dateKey] || []).map((t) => t.padStart(5, '0'));
  const avail = (availableSlots[dateKey] || []).map((t) => t.padStart(5, '0'));
  const selCount = selectedDates.reduce((c, d) => {
    const k = formatDateKey(d);
    return c + ((selectedTimes[k] || []).length > 0 ? 1 : 0);
  }, 0);
  const remCount = selectedDates.length - selCount;

  return (
    <div className='w-full p-6 border rounded-lg shadow-md bg-white mt-5'>
      <h2 className='text-lg text-center font-semibold mb-4'>
        Select Time Slots
      </h2>

      <div className='mb-4 text-xs text-center text-gray-600'>
        { remCount > 0 ? (
          <span className='text-red-600 bg-red-600/10 p-2 rounded-lg'>
            You have selected time slots for { selCount } out of{ ' ' }
            { selectedDates.length } date(s). Please complete the remaining{ ' ' }
            { remCount } date
            { remCount > 1 ? 's' : '' }.
          </span>
        ) : (
          <span className='text-green-600 bg-green-600/10 p-2 rounded-lg'>
            All { selectedDates.length } date{ selectedDates.length > 1 ? 's' : '' }{ ' ' }
            have selected time slots.
          </span>
        ) }
      </div>

      <div className='flex justify-between items-center mb-4'>
        <FloatingLabelSelect
            label='Interview Duration'
            required
            isMulti={false}
            value={durationOptions.find((o) => o.value === duration)}
            onChange={(opt) => {
            // Only allow duration change if no slots are selected
              const hasSelectedSlots = Object.values(selectedTimes).some(
                (times) => times.length > 0,
              );
              if (!hasSelectedSlots) {
                setDuration(opt?.value || meetingDuration.toString());
              }
            }}
            options={durationOptions}
            isDisabled={Object.values(selectedTimes).some(
              (times) => times.length > 0,
            )}
        />
        <div className='flex items-center space-x-2'>
          <button
              onClick={() => idx > 0 && setIdx(idx - 1)}
              disabled={idx === 0}
              className='p-2 rounded-full bg-gray-100 disabled:bg-gray-50'
          >
            <ChevronLeft />
          </button>
          <span className='text-sm text-gray-500'>
            { date.toLocaleDateString(undefined, {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            }) }
          </span>
          <button
              onClick={() => idx < selectedDates.length - 1 && setIdx(idx + 1)}
              disabled={idx === selectedDates.length - 1}
              className='p-2 rounded-full bg-gray-100 disabled:bg-gray-50'
          >
            <ChevronRight />
          </button>
        </div>
      </div>

      <div className='flex space-x-2 mb-4'>
        { (['Main', 'Early', 'Late'] as const).map((x) => (
          <button
              key={x}
              onClick={() => setTab(x)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                tab === x
                  ? 'bg-primary-dark text-white'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
          >
            {
              {
                Main: '6:00 AM - 6:00 PM',
                Early: '3:00 AM - 6:00 AM',
                Late: '6:00 PM - 9:00 PM',
              }[x]
            }
          </button>
        )) }
      </div>

      <div className='grid grid-cols-4 gap-3 overflow-x-auto py-4'>
        { slots.map((slot: TimeSlot) => {
          const isSel = selTimes.includes(slot.start);
          const isBkd = booked.includes(slot.start);
          const isAvl = avail.length ? avail.includes(slot.start) : true;
          const disabled = isBkd || !isAvl;

          return (
            <button
                key={`${slot.start}-${slot.end}`}
                disabled={disabled}
                onClick={() => {
                  if (!disabled) {
                    const interval = Number(duration) >= 30 ? 30 : 15;
                    const step = Number(duration) / interval; // Calculate steps based on the interval
                    const slotIndex = slots.findIndex(
                      (s) => s.start === slot.start,
                    );
                    const slotsToSelect = slots.slice(
                      slotIndex,
                      slotIndex + step,
                    );

                    // Validate that all slots in the range are available
                    const allSlotsAvailable = slotsToSelect.every(
                      (s) =>
                        !booked.includes(s.start) &&
                      (avail.length === 0 || avail.includes(s.start)),
                    );

                    if (allSlotsAvailable) {
                      slotsToSelect.forEach((s) => onTimeSelect(date, s.start));
                    }
                  }
                }}
                className={`
                p-2 text-xs text-center rounded-lg border
                ${
                  disabled
                    ? 'bg-orange-400/40 outline outline-orange-400 text-red-800 cursor-not-allowed'
                    : isSel
                      ? 'bg-primary-dark text-white'
                      : 'hover:bg-gray-200'
                }
              `}
            >
              { slot.display }
            </button>
          );
        }) }
      </div>

      { error && helperText && (
        <p className='text-sm text-red-600 mt-2'>{ helperText }</p>
      ) }
    </div>
  );
};
